import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  LinearProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Rating,
  Alert,
  Snackbar,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Badge, Slider
} from "@mui/material";
import {
  People,
  Assignment,
  RateReview,
  CheckCircle,
  Pending,
  Edit,
  Visibility,
  Send,
  ArrowBack,
  ThumbUp,
  ThumbDown,
  Star,
  TrendingUp,
  Download,
  FilterList,
  Search,
  Refresh,
} from "@mui/icons-material";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
const api = axios.create({ baseURL: API_URL });

// Add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const ManagerReviewDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [teamData, setTeamData] = useState(null);
  const [selectedAppraisal, setSelectedAppraisal] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
      const [error, setError] = useState('');
      const [employeeId, setEmployeeId] = useState(null);
  
  // Review form states
  const [managerRatings, setManagerRatings] = useState({});
  const [managerComments, setManagerComments] = useState("");
  const [overallManagerRating, setOverallManagerRating] = useState(0);
  const [reviewStep, setReviewStep] = useState(0);
  const [workSummaryReviews, setWorkSummaryReviews] = useState({});
const steps = ['Review Goals', 'Review Work Summaries', 'Overall Rating'];
  const getCompanyId = () => {
    const token = localStorage.getItem("token");
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.company_id;
    } catch (error) {
      //console.error("Error parsing token:", error);
      return null;
    }
  };
  const fetchCurrentUser = async () => {
    try {
       const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      const response = await axios.get(`${API_URL}/api/user/current?company_id=${company_id}`, {
        withCredentials: true
      });

      if (!response.data || !response.data.employee_id) {
        throw new Error('Employee ID not found in user data');
      }

      setEmployeeId(response.data.employee_id);
      
      return response.data.employee_id;
    } catch (err) {
      //console.error("Error fetching current user:", err);
      setError('Failed to fetch user information');
      return null;
    }
  };
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  // Fetch team appraisals
  const fetchTeamAppraisals = async () => {
    try {
      const token = localStorage.getItem('token');
      setLoading(true);
      const company_id = getCompanyId();
      const employee_id = await fetchCurrentUser();
      if (!company_id) {
        showSnackbar("Please login again", "error");
        return;
      }

      const response = await api.get(`/api/manager/review/team?employee_id=${employee_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
           
          }
        }
      );
      setTeamData(response.data);
    } catch (error) {
      //console.error("Error fetching team appraisals:", error);
      showSnackbar(error.response?.data?.message || "Error loading data", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeamAppraisals();
  }, []);

  // Open review dialog
  const handleOpenReview = async (appraisalId) => {
    try {
      const token = localStorage.getItem('token');
      setLoading(true);
      const company_id = getCompanyId();
            const employee_id = await fetchCurrentUser();

      const response = await api.get(`/api/manager/review/${appraisalId}?employee_id=${employee_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          
          }
        }
      );
      
      setSelectedAppraisal(response.data);
      setReviewDialogOpen(true);
      setReviewStep(0);
      
      // Initialize manager ratings
      const ratings = {};
      response.data.ratings.forEach(rating => {
        ratings[rating.id] = {
          manager_rating: rating.manager_rating || 0,
          manager_comments: rating.manager_comments || ""
        };
      });
      setManagerRatings(ratings);
      setManagerComments(response.data.selfAppraisal.manager_comments || "");
      setOverallManagerRating(response.data.selfAppraisal.manager_rating || 0);
      
    } catch (error) {
      //console.error("Error opening review:", error);
      showSnackbar("Error loading appraisal details", "error");
    } finally {
      setLoading(false);
    }
  };

  // Update manager rating for a goal
  const updateRating = async (ratingId, updates) => {
    try {
      const token = localStorage.getItem('token');
      const company_id = getCompanyId();
            const employee_id = await fetchCurrentUser();

      await api.put(
        `/api/manager/review/${selectedAppraisal.selfAppraisal.id}/ratings/${ratingId}?employee_id=${employee_id}`,
        updates,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      
      showSnackbar("Rating saved", "success");
      return true;
    } catch (error) {
      //console.error("Error updating rating:", error);
      showSnackbar("Error saving rating", "error");
      return false;
    }
  };

  // Save overall feedback
  const saveOverallFeedback = async () => {
    try {
      const token = localStorage.getItem('token');
      const company_id = getCompanyId();
            const employee_id = await fetchCurrentUser();

      await api.put(
        `/api/manager/review/${selectedAppraisal.selfAppraisal.id}/feedback?employee_id=${employee_id}`,
        {
          manager_comments: managerComments,
          manager_rating: overallManagerRating
        },{
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      
      showSnackbar("Feedback saved", "success");
      return true;
    } catch (error) {
      //console.error("Error saving feedback:", error);
      showSnackbar("Error saving feedback", "error");
      return false;
    }
  };
// Add this function in ManagerReviewDashboard:
const updateWorkSummaryReview = async (summaryId, manager_review) => {
  try {
    const token = localStorage.getItem('token');
    const company_id = getCompanyId();
    
    await api.put(
      `/api/manager/review/${selectedAppraisal.selfAppraisal.id}/work-summaries/${summaryId}`,
      { manager_review },{
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      }
    );
    
    showSnackbar("Review saved", "success");
    return true;
  } catch (error) {
    //console.error("Error updating work summary review:", error);
    showSnackbar("Error saving review", "error");
    return false;
  }
};
  // Complete review
  const handleCompleteReview = async () => {
    try {
      const token = localStorage.getItem('token');
      setLoading(true);
      const company_id = getCompanyId();
            const employee_id = await fetchCurrentUser();

      await api.post(
        `/api/manager/review/${selectedAppraisal.selfAppraisal.id}/complete?employee_id=${employee_id}`,
        { final_comments: managerComments ,manager_rating:overallManagerRating },{
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      
      showSnackbar("Review completed successfully!", "success");
      setReviewDialogOpen(false);
      fetchTeamAppraisals(); // Refresh data
      
    } catch (error) {
      //console.error("Error completing review:", error);
      showSnackbar(error.response?.data?.message || "Error completing review", "error");
    } finally {
      setLoading(false);
    }
  };

  // Reopen appraisal
  const handleReopenAppraisal = async (appraisalId) => {
    if (!window.confirm("Are you sure you want to reopen this appraisal? The employee will be able to make changes.")) return;
    
    try {
      const company_id = getCompanyId();
            const employee_id = await fetchCurrentUser();
            const token = localStorage.getItem('token');
      await api.post(
        `/api/manager/review/${appraisalId}/reopen?employee_id=${employee_id}`,
        { reason: "Requested changes"},{
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      
      showSnackbar("Appraisal reopened", "success");
      fetchTeamAppraisals();
    } catch (error) {
      //console.error("Error reopening appraisal:", error);
      showSnackbar("Error reopening appraisal", "error");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Draft": return "default";
      case "Submitted": return "warning";
      case "Under Review": return "info";
      case "Completed": return "success";
      case "Reopened": return "error";
      default: return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Submitted": return <Pending color="warning" />;
      case "Under Review": return <RateReview color="info" />;
      case "Completed": return <CheckCircle color="success" />;
      default: return <Assignment color="action" />;
    }
  };

  // Render team dashboard
  const renderDashboard = () => {
    if (!teamData) return null;

    return (
      <Grid container spacing={3}>
        {/* Statistics Cards */}
        <Grid item xs={12}>
          <Card elevation={3}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h6" gutterBottom>
                    <People sx={{ verticalAlign: "middle", mr: 1 }} />
                    Team Performance Reviews
                  </Typography>
                  {/* <Typography variant="body2" color="text.secondary">
                    {teamData.manager.name} • {teamData.manager.department} • {teamData.manager.team_size} team members
                  </Typography> */}
                </Box>
               {teamData?.activeCycle?.name && (
  <Chip
    label={`Cycle: ${teamData.activeCycle.name}`}
    color="primary"
    variant="outlined"
  />
)}

              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: "center" }}>
                      <Typography variant="h3" color="text.secondary">
                        {teamData.statistics?.total_employees}
                      </Typography>
                      <Typography variant="body2">Team Members</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: "center" }}>
                      <Typography variant="h3" color="warning.main">
                        {teamData.statistics?.submitted}
                      </Typography>
                      <Typography variant="body2">Submitted</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: "center" }}>
                      <Typography variant="h3" color="info.main">
                        {teamData.statistics?.under_review}
                      </Typography>
                      <Typography variant="body2">Under Review</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: "center" }}>
                      <Typography variant="h3" color="success.main">
                        {teamData.statistics?.completed}
                      </Typography>
                      <Typography variant="body2">Completed</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" gutterBottom>
                  Overall Completion: {teamData.statistics?.completion_rate}%
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={teamData.statistics?.completion_rate}
                  sx={{ height: 10, borderRadius: 5 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Tabs for different statuses */}
        <Grid item xs={12}>
          <Paper elevation={2}>
                       <Tabs 
              value={activeTab} 
              onChange={(e, newValue) => setActiveTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab label={`Submitted (${teamData.statistics?.submitted})`} />
              <Tab label={`Under Review (${teamData.statistics?.under_review})`} />
              <Tab label={`Completed (${teamData.statistics?.completed})`} />
              {/* <Tab label={`All (${teamData.appraisals?.length})`} /> */}
            </Tabs>

            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Self Rating</TableCell>
                    {/* <TableCell>Goals</TableCell> */}
                    <TableCell>Submitted On</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {teamData?.appraisals
                    .filter(appraisal => {
                      if (activeTab === 0) return appraisal.status === 'Submitted';
                      if (activeTab === 1) return appraisal.status === 'Under Review';
                      if (activeTab === 2) return appraisal.status === 'Completed';
                      return true;
                    })
                    .map((appraisal) => (
                      <TableRow key={appraisal.id} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <Avatar sx={{ bgcolor: "#1976d2", width: 32, height: 32, mr: 2 }}>
                              {appraisal.employee_name?.charAt(0) || "E"}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {appraisal.employee_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {appraisal.employee_position}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>{appraisal.employee_department}</TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(appraisal.status)}
                            label={appraisal.status}
                            color={getStatusColor(appraisal.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <Star sx={{ color: "#ff9800", fontSize: 16, mr: 0.5 }} />
                            <Typography>{appraisal.overall_self_rating?.toFixed(1) || "0.0"}/5</Typography>
                          </Box>
                        </TableCell>
                        {/* <TableCell>
                          <Typography variant="body2">
                            {appraisal.goals_count || "N/A"}
                          </Typography>
                        </TableCell> */}
                        <TableCell>
                          {appraisal.submitted_at ? (
                            new Date(appraisal.submitted_at).toLocaleDateString()
                          ) : (
                            "Not submitted"
                          )}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={1}>
                            <Tooltip title="Review">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenReview(appraisal.id)}
                                disabled={appraisal.status === 'Draft'}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            
                            {appraisal.status === 'Submitted' && (
                              <Tooltip title="Start Review">
                                <Button
                                  size="small"
                                  variant="contained"
                                  startIcon={<RateReview />}
                                  onClick={() => handleOpenReview(appraisal.id)}
                                >
                                  Review
                                </Button>
                              </Tooltip>
                            )}
                            
                            {appraisal.status === 'Under Review' && (
                              <Tooltip title="Continue Review">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  onClick={() => handleOpenReview(appraisal.id)}
                                >
                                  Continue
                                </Button>
                              </Tooltip>
                            )}
                            
                            {(appraisal.status === 'Submitted' || appraisal.status === 'Under Review') && (
                              <Tooltip title="Reopen for Employee">
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() => handleReopenAppraisal(appraisal.id)}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    );
  };
const renderStep1 = () => {
  if (!selectedAppraisal) return null;
  const overAllEmployeeSummary=selectedAppraisal.selfAppraisal.overall_summary
  const workSummaries = selectedAppraisal.selfAppraisal.work_summaries || [];
  
  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom color="primary">
        Review Work Summaries ({workSummaries.length} total)
      </Typography>
      
      {workSummaries.map((summary, index) => (
        <Card key={summary.id} variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom color="primary">
              Work Summary {index + 1}: {summary.project_name}
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2">
                  <strong>Period:</strong> {new Date(summary.start_date).toLocaleDateString()}
                  {summary.end_date ? ` to ${new Date(summary.end_date).toLocaleDateString()}` : ' to Present'}
                </Typography>
                {summary.manager_name && (
                  <Typography variant="body2">
                    <strong>Manager:</strong> {summary.manager_name}
                  </Typography>
                )}
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="body2" gutterBottom>
                  <strong>Employee's Description:</strong>
                </Typography>
                <Typography variant="body2" paragraph sx={{ p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  {summary.work_description}
                </Typography>
              </Grid>
              
              {summary.key_highlights && (
                <Grid item xs={12}>
                  <Typography variant="body2" gutterBottom>
                    <strong>Key Highlights:</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    {summary.key_highlights}
                  </Typography>
                </Grid>
              )}
              
              {summary.challenges_faced && (
                <Grid item xs={12}>
                  <Typography variant="body2" gutterBottom>
                    <strong>Challenges Faced:</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    {summary.challenges_faced}
                  </Typography>
                </Grid>
              )}
              
              {summary.actions_taken && (
                <Grid item xs={12}>
                  <Typography variant="body2" gutterBottom>
                    <strong>Actions Taken:</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    {summary.actions_taken}
                  </Typography>
                </Grid>
              )}
              
              <Grid item xs={12}>
                <Typography variant="body2" gutterBottom>
                  <strong>Your Review / Feedback</strong>
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={workSummaryReviews[summary.id] || summary.manager_review || ""}
                  onChange={(e) => {
                    setWorkSummaryReviews(prev => ({
                      ...prev,
                      [summary.id]: e.target.value
                    }));
                  }}
                  onBlur={() => {
                    if (workSummaryReviews[summary.id] !== undefined) {
                      updateWorkSummaryReview(summary.id, workSummaryReviews[summary.id]);
                    }
                  }}
                  placeholder="Provide your feedback on this work. Recognize achievements, suggest improvements..."
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}
      {overAllEmployeeSummary && (
        <Grid item xs={12}>
                <Typography variant="body2" gutterBottom>
                  <strong>Employee's Overall Summary:</strong>
                </Typography>
                <Typography variant="body2" paragraph sx={{ p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  {overAllEmployeeSummary}
                </Typography>
              </Grid>
      )}
      
      <Box display="flex" justifyContent="space-between" mt={3}>
        <Button
          variant="outlined"
          onClick={() => setReviewStep(0)}
        >
          Back to Goals
        </Button>
        <Button
          variant="contained"
          onClick={() => setReviewStep(2)}
        >
          Continue to Overall Rating
        </Button>
      </Box>
    </Box>
  );
};
  // Render review dialog
  const renderReviewDialog = () => {
    if (!selectedAppraisal) return null;

const steps = ['Review Goals', 'Review Work Summaries', 'Overall Rating'];
    return (
      <Dialog open={reviewDialogOpen} onClose={() => setReviewDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Review: {selectedAppraisal.employee.name}
            </Typography>
            <Chip 
              label={selectedAppraisal.selfAppraisal.status} 
              color={getStatusColor(selectedAppraisal.selfAppraisal.status)}
              size="small"
            />
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {/* Stepper */}
          <Box sx={{ mb: 3 }}>
            <Stepper activeStep={reviewStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
          
          {/* Step 0: Review Goals */}
          {reviewStep === 0 && (
            <Box>
              <Typography variant="subtitle1" gutterBottom color="primary">
                Goal-wise Review ({selectedAppraisal.ratings.length} goals)
              </Typography>
              
              {selectedAppraisal.ratings.map((rating) => (
                <Card key={rating.id} variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2">
                          {rating.goal?.title || "Goal"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          {rating.goal?.description || "No description"}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" gutterBottom>
                          <strong>Employee's Rating:</strong> {rating.self_rating || "Not rated"}/5
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          <strong>Achievement:</strong> {rating.achievement_percentage || 0}%
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          <strong>Employee Comments:</strong> {rating.self_comments || "No comments"}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" gutterBottom>
                          <strong>Your Rating</strong>
                        </Typography>
                        <Rating
                          value={managerRatings[rating.id]?.manager_rating || 0}
                          onChange={(event, newValue) => {
                            setManagerRatings(prev => ({
                              ...prev,
                              [rating.id]: {
                                ...prev[rating.id],
                                manager_rating: newValue
                              }
                            }));
                            updateRating(rating.id, { manager_rating: newValue });
                          }}
                          precision={0.5}
                          size="large"
                        />
                        
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          label="Your Comments"
                          value={managerRatings[rating.id]?.manager_comments || ""}
                          onChange={(e) => {
                            setManagerRatings(prev => ({
                              ...prev,
                              [rating.id]: {
                                ...prev[rating.id],
                                manager_comments: e.target.value
                              }
                            }));
                          }}
                          onBlur={() => updateRating(rating.id, { 
                            manager_comments: managerRatings[rating.id]?.manager_comments 
                          })}
                          sx={{ mt: 1 }}
                          placeholder="Provide feedback on this goal..."
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
              
              <Box display="flex" justifyContent="space-between" mt={3}>
                <Button
                  variant="outlined"
                  onClick={() => setReviewDialogOpen(false)}
                >
                  Close
                </Button>
                 <Button
    variant="contained"
    onClick={() => {
      // Check if there are work summaries
      const hasWorkSummaries = selectedAppraisal.selfAppraisal.work_summaries?.length > 0;
      setReviewStep(hasWorkSummaries ? 1 : 2); // Skip to step 2 if no work summaries
    }}
  >
    {selectedAppraisal.selfAppraisal.work_summaries?.length > 0 
      ? "Continue to Work Summaries" 
      : "Continue to Overall Rating"}
  </Button>
              </Box>
            </Box>
          )}
          
          {/* Step 1: Overall Feedback */}
          {reviewStep === 1 && renderStep1()}
          
     

          {reviewStep === 2 && (
  <Box>
    <Typography variant="subtitle1" gutterBottom color="primary">
      Final Overall Rating
    </Typography>
    
    <Alert severity="info" sx={{ mb: 3 }}>
      <Typography variant="body2">
        Please review all goals and work summaries before providing the final rating.
      </Typography>
    </Alert>
    
    {/* Show summary statistics */}
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="subtitle2" gutterBottom>
          Review Summary
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <Typography variant="caption" color="text.secondary">Goals</Typography>
            <Typography variant="h6">{selectedAppraisal.statistics.total_goals}</Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="caption" color="text.secondary">Work Summaries</Typography>
            <Typography variant="h6">{selectedAppraisal.selfAppraisal.work_summaries?.length || 0}</Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="caption" color="text.secondary">Avg Goal Rating</Typography>
            <Typography variant="h6">
              {Object.values(managerRatings).reduce((sum, r) => sum + (r.manager_rating || 0), 0) / 
               Object.values(managerRatings).filter(r => r.manager_rating).length || 0}
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="caption" color="text.secondary">Overall Rating</Typography>
            <Typography variant="h6">{overallManagerRating}/5</Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
    
    {/* Overall Rating Input */}
    <Box sx={{ mb: 3 }}>
      <Typography variant="body2" gutterBottom>
        <strong>Final Overall Rating (1-5)</strong>
      </Typography>
      <Box display="flex" alignItems="center" gap={2}>
        <Rating
          value={overallManagerRating}
          onChange={(event, newValue) => setOverallManagerRating(newValue)}
          precision={0.5}
          size="large"
        />
        <Typography variant="body2" color="text.secondary">
          {overallManagerRating}/5
        </Typography>
      </Box>
    </Box>
    
    {/* Final Comments */}
    <TextField
      fullWidth
      multiline
      rows={4}
      label="Final Comments"
      value={managerComments}
      onChange={(e) => setManagerComments(e.target.value)}
      placeholder="Provide your final assessment and recommendations..."
      sx={{ mb: 3 }}
    />
    
    <Box display="flex" justifyContent="space-between" mt={3}>
      <Button
        variant="outlined"
        onClick={() => setReviewStep(1)}
      >
        Back to Work Summaries
      </Button>
      {selectedAppraisal.selfAppraisal.status!=="Completed"&&(
      <Button
        variant="contained"
        color="success"
        startIcon={<CheckCircle />}
        onClick={handleCompleteReview}
        disabled={loading}
      >
        {loading ? "Finalizing..." : "Complete Review"}
      </Button>)}
    </Box>
  </Box>
)}
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" gutterBottom fontWeight="bold">
             Self-Appraisal Reviews
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Review and provide feedback on your team's self-appraisals
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchTeamAppraisals}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Paper>
      
      {/* Main Content */}
      {loading && !teamData ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : teamData ? (
        renderDashboard()
      ) : (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary">
            No team data available
          </Typography>
        </Paper>
      )}
      
      {/* Review Dialog */}
      {renderReviewDialog()}
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ManagerReviewDashboard;